<template>
	<div class="content">
		<div class="section">
			<div class="section-tit">게시판</div>
			<div class="search-top">
				<div class="left">
					<label class="input-label">검색</label>
					<select v-model="searchInfo_.mode" class="form-control select sm">
						<option value="title">제목</option>
						<option value="memo">내용</option>
						<option value="mem">작성자</option>
					</select>
					<input class="form-control" type="text" v-model="searchInfo_.keyword" @keyup.enter="btnSearch" />
					<button type="button" class="btn search" @click="btnSearch"><b-icon-search /></button>
				</div>
				<div class="right">
					<button type="button" class="btn btn-sm btn-secondary" @click="btnSearchReset">초기화</button>
				</div>
			</div>
		</div>

		<div class="section">
			<div class="tab-btn">
				<button
					v-for="c in cateList"
					:key="'cate_' + c.board_category_uid"
					type="button"
					class="btn tab"
					v-bind:class="{ active: cateUid == c.board_category_uid }"
					@click="changeCate(c.board_category_uid)"
				>
					{{ c.name }}
				</button>
				<div class="right">
					<button
						v-if="currentCate && currentCate.can_write"
						type="button"
						class="btn btn-sm btn-primary"
						@click="btnWrite"
					>
						글쓰기
					</button>
				</div>
			</div>

			<!-- B-13: 추천매물·매수요청은 상태별 탭. '거래완료/완료' 는 별도 탭으로 분리한다. -->
			<div v-if="isItemCate || isRequestCate" class="tab-btn sm">
				<button
					v-for="s in statusTabs"
					:key="'st_' + s.value"
					type="button"
					class="btn tab"
					v-bind:class="{ active: searchInfo.status === s.value }"
					@click="changeStatus(s.value)"
				>
					{{ s.label }}
				</button>
			</div>

			<div class="tab-con">
				<div class="table-wrap">
					<!-- 추천매물: 매물 정보를 조회 시점에 조인해 항상 최신값을 보여준다 (B-12) -->
					<table v-if="isItemCate" class="table click">
						<colgroup>
							<col width="6%" /><col width="20%" /><col width="16%" /><col width="9%" />
							<col width="7%" /><col width="7%" /><col width="7%" /><col width="7%" />
							<col width="7%" /><col width="7%" /><col width="7%" />
						</colgroup>
						<tr>
							<th>번호</th>
							<th>제목</th>
							<th>물건명 / 주소</th>
							<th>매매가</th>
							<th>평당가</th>
							<th>대지(평)</th>
							<th>연면적(평)</th>
							<th>수익률</th>
							<th>담당자</th>
							<th>상태</th>
							<th>작성자</th>
						</tr>
						<tr
							v-for="(item, idx) in itemList"
							:key="'bp_' + item.board_post_uid"
							v-bind:class="{ bg: item.pin_flag === 'Y' }"
							@click="btnOnDetail(item.board_post_uid)"
						>
							<td>
								<span v-if="item.pin_flag === 'Y'" class="txt-c--red">고정</span>
								<span v-else>{{ rowNo(idx) }}</span>
							</td>
							<td class="txt-left">
								<span v-if="isNewPost(item)" class="badge-new">새글</span>
								{{ item.title }}
								<span v-if="item.comment_cnt" class="txt-c--blue">[{{ item.comment_cnt }}]</span>
								<b-icon-paperclip v-if="item.file_cnt" />
								<span v-if="item.like_cnt" class="txt-c--red">♥{{ item.like_cnt }}</span>
							</td>
							<td class="txt-left">
								<template v-if="item.item_name">
									{{ item.item_name }}<br /><span class="txt-c--grey">{{ $normalizeAddr(item.item_jibun_addr) }}</span>
								</template>
								<span v-else class="txt-c--red">삭제된 매물</span>
							</td>
							<td>
								{{ $formatMoney(item.item_sell_price, $MONEY_FORMAT_TYPE.MUCH) }}
								<!-- B-12(선택): 등록시점 대비 변동 표시 -->
								<span v-if="priceDiff(item)" v-bind:class="priceDiff(item).cls" class="d-block">
									{{ priceDiff(item).text }}
								</span>
							</td>
							<td>{{ $formatNumber(item.item_py_price) }}</td>
							<td>{{ $formatArea(item.item_land_size_p) }}</td>
							<td>{{ $formatArea(item.item_total_size_p) }}</td>
							<td>{{ item.item_income_rate != null ? item.item_income_rate + '%' : '' }}</td>
							<td>{{ item.item_mgr_name }}</td>
							<td>
								<span v-bind:class="statusClass(item.recommend_status_view)">
									{{ RECO_STATUS[item.recommend_status_view] }}
								</span>
							</td>
							<td>{{ item.reg_mem_name }} {{ item.reg_mem_rank_name }}</td>
						</tr>
						<tr v-if="!itemList.length">
							<td colspan="11">등록된 추천매물이 없습니다.</td>
						</tr>
					</table>

					<!-- 매수요청: 조건 요약 + 연결된 추천매물 수. 매물 연결은 상세에서 이뤄진다. -->
					<table v-else-if="isRequestCate" class="table click">
						<colgroup>
							<col width="6%" /><col width="22%" /><col width="11%" /><col width="12%" />
							<col width="10%" /><col width="9%" /><col width="7%" /><col width="7%" />
							<col width="9%" /><col width="7%" />
						</colgroup>
						<tr>
							<th>번호</th>
							<th>제목</th>
							<th>예산</th>
							<th>희망 지역</th>
							<th>용도</th>
							<th>희망 면적</th>
							<th>추천</th>
							<th>상태</th>
							<th>작성자</th>
							<th>등록일</th>
						</tr>
						<tr
							v-for="(item, idx) in itemList"
							:key="'bp_' + item.board_post_uid"
							v-bind:class="{ bg: item.pin_flag === 'Y' }"
							@click="btnOnDetail(item.board_post_uid)"
						>
							<td>
								<span v-if="item.pin_flag === 'Y'" class="txt-c--red">고정</span>
								<span v-else>{{ rowNo(idx) }}</span>
							</td>
							<td class="txt-left">
								<span v-if="isNewPost(item)" class="badge-new">새글</span>
								{{ item.title }}
								<span v-if="item.comment_cnt" class="txt-c--blue">[{{ item.comment_cnt }}]</span>
								<b-icon-paperclip v-if="item.file_cnt" />
							</td>
							<td class="txt-left">{{ item.req_budget || '-' }}</td>
							<td class="txt-left">{{ item.req_region || '-' }}</td>
							<td class="txt-left">{{ item.req_use || '-' }}</td>
							<td class="txt-left">{{ item.req_area || '-' }}</td>
							<td>
								<span v-if="item.request_item_cnt" class="txt-c--blue">{{ item.request_item_cnt }}건</span>
								<span v-else class="txt-c--grey">-</span>
							</td>
							<td>
								<span v-bind:class="statusClass(item.recommend_status_view)">
									{{ REQ_STATUS[item.recommend_status_view] }}
								</span>
							</td>
							<td>{{ item.reg_mem_name }} {{ item.reg_mem_rank_name }}</td>
							<td>{{ $dateFormat(item.reg_date) }}</td>
						</tr>
						<tr v-if="!itemList.length">
							<td colspan="10">등록된 매수요청이 없습니다.</td>
						</tr>
					</table>

					<!-- 일반 게시판 (공지사항 / 교육자료 / 인사·일정) -->
					<table v-else class="table click">
						<colgroup>
							<col width="7%" /><col width="47%" /><col width="14%" />
							<col width="12%" /><col width="8%" /><col width="12%" />
						</colgroup>
						<tr>
							<th>번호</th>
							<th>제목</th>
							<th>작성자</th>
							<th>등록일</th>
							<th>조회</th>
							<th>확인</th>
						</tr>
						<tr
							v-for="(item, idx) in itemList"
							:key="'bp_' + item.board_post_uid"
							v-bind:class="{ bg: item.pin_flag === 'Y' }"
							@click="btnOnDetail(item.board_post_uid)"
						>
							<td>
								<span v-if="item.pin_flag === 'Y'" class="txt-c--red">고정</span>
								<span v-else>{{ rowNo(idx) }}</span>
							</td>
							<td class="txt-left">
								<!-- CE 새글(hit) 표시: 남이 쓴 글 중 내가 아직 안 연 글. 열람하면 사라진다. -->
								<span v-if="isNewPost(item)" class="badge-new">새글</span>
								<span v-if="item.notice_all_flag === 'Y'" class="txt-c--red">[전체공지]</span>
								{{ item.title }}
								<span v-if="item.comment_cnt" class="txt-c--blue">[{{ item.comment_cnt }}]</span>
								<b-icon-paperclip v-if="item.file_cnt" />
							</td>
							<td>{{ item.reg_mem_name }} {{ item.reg_mem_rank_name }}</td>
							<td>{{ $datetimeFormat(item.reg_date) }}</td>
							<td>{{ item.view_cnt }}</td>
							<td>
								<!-- B-5: 확인 N / 전체 M. 클릭하면 미확인자 명단 팝업 (작성자·관리자만) -->
								<template v-if="item.confirm_flag === 'Y'">
									<button type="button" class="btn btn-xsm btn-light" @click.stop="openReadList(item)">
										{{ item.confirm_cnt }} / {{ item.total_mem_cnt }}
									</button>
									<span v-if="!item.my_confirm_date" class="txt-c--red d-block">미확인</span>
								</template>
								<span v-else class="txt-c--grey">-</span>
							</td>
						</tr>
						<tr v-if="!itemList.length">
							<td colspan="6">등록된 게시글이 없습니다.</td>
						</tr>
					</table>
				</div>

				<ul class="pagination b-pagination pagination-sm" v-if="pageData != null && pageData.list.length">
					<li class="page-item" v-bind:class="{ disabled: 1 == page }">
						<button type="button" class="page-link" @click="getItemList(1)"><b-icon-chevron-double-left /></button>
					</li>
					<li class="page-item" v-bind:class="{ disabled: pageData.first == null }">
						<button type="button" class="page-link" @click="pageData.first !== null ? getItemList(pageData.first) : ''">
							<b-icon-chevron-left />
						</button>
					</li>
					<li
						v-for="pg in pageData.list"
						:key="'pg_' + pg"
						class="page-item"
						v-bind:class="{ active: pageData.currentPage == pg }"
					>
						<button type="button" class="page-link" @click="getItemList(pg)">{{ pg }}</button>
					</li>
					<li class="page-item" v-bind:class="{ disabled: pageData.end == null }">
						<button type="button" class="page-link" @click="pageData.end !== null ? getItemList(pageData.end) : ''">
							<b-icon-chevron-right />
						</button>
					</li>
					<li class="page-item" v-bind:class="{ disabled: pageData.totalPage == page }">
						<button type="button" class="page-link" @click="getItemList(pageData.totalPage)">
							<b-icon-chevron-double-right />
						</button>
					</li>
				</ul>
			</div>
		</div>

		<!-- B-5 확인현황 팝업 -->
		<div v-if="readPopup" class="popup-wrap">
			<div class="dim" @click="readPopup = null"></div>
			<div class="popup sm">
				<div class="popup-tit">
					확인현황 — {{ readPopup.title }}
					<button type="button" class="btn btn-close" @click="readPopup = null"><b-icon-x-lg /></button>
				</div>
				<div class="popup-con">
					<div class="section-tit sub">
						확인 {{ readInfo.confirmed }} / 전체 {{ readInfo.total }}명
						<span class="sub">열람만 함 {{ readInfo.viewed_only }}명</span>
					</div>
					<div class="table-wrap">
						<table class="table sm">
							<colgroup>
								<col width="20%" /><col width="18%" /><col width="15%" /><col width="23%" /><col width="24%" />
							</colgroup>
							<tr>
								<th>이름</th>
								<th>팀</th>
								<th>직급</th>
								<th>열람일시</th>
								<th>확인일시</th>
							</tr>
							<tr v-for="r in readInfo.list" :key="'rd_' + r.mem_uid">
								<td>{{ r.mem_name }}</td>
								<td>{{ r.team_name }}</td>
								<td>{{ r.mem_rank_name }}</td>
								<td>{{ r.view_date ? $datetimeFormat(r.view_date) : '-' }}</td>
								<td>
									<span v-if="r.confirm_date" class="txt-c--blue">{{ $datetimeFormat(r.confirm_date) }}</span>
									<span v-else class="txt-c--red">미확인</span>
								</td>
							</tr>
						</table>
					</div>
					<div class="btn-wrap">
						<button type="button" class="btn btn-sm btn-secondary" @click="readPopup = null">닫기</button>
						<button
							type="button"
							class="btn btn-sm btn-primary"
							v-if="readInfo.total - readInfo.confirmed > 0"
							:disabled="reminding"
							@click="btnRemind"
						>
							{{ reminding ? '발송 중…' : `미확인자 ${readInfo.total - readInfo.confirmed}명에게 재알림` }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
// B-13 추천 상태 라벨. 서버가 recommend_status_view 로 "수동 오버라이드 없으면 매물 상태 반영"
// 까지 계산해서 내려주므로 화면은 라벨만 붙인다.
const RECO_STATUS = { open: '추천중', hold: '보류', done: '거래완료' };
// 매수요청 상태 라벨 — 같은 컬럼(open|hold|done)을 쓰지만 화면 표현이 다르다.
const REQ_STATUS = { open: '구하는중', hold: '보류', done: '완료' };

export default {
	name: 'Board',
	data() {
		return {
			RECO_STATUS,
			REQ_STATUS,
			cateList: [],
			cateUid: null,
			itemList: [],

			searchInfo: { mode: 'title', keyword: '', status: '' },
			searchInfo_: { mode: 'title', keyword: '' },

			page: 1,
			pageData: null,
			totalCount: 0,
			itemSize: 20,
			blockSize: 5,

			readPopup: null,
			readInfo: { list: [], total: 0, confirmed: 0, viewed_only: 0 },
			// 재알림 이중발송 잠금 + 목록 응답 경합(늦게 도착한 이전 요청이 현재
			// 탭/검색 결과를 덮어쓰는 것) 방지용 요청 세대 번호 (Codex 헌트 P2 2건)
			reminding: false,
			listSeq: 0,
		};
	},
	computed: {
		currentCate() {
			return this.cateList.find(c => c.board_category_uid == this.cateUid) || null;
		},
		isItemCate() {
			return !!(this.currentCate && this.currentCate.item_flag === 'Y');
		},
		isRequestCate() {
			return !!(this.currentCate && this.currentCate.request_flag === 'Y');
		},
		statusTabs() {
			const labels = this.isRequestCate ? REQ_STATUS : RECO_STATUS;
			return [
				{ value: '', label: '전체' },
				{ value: 'open', label: labels.open },
				{ value: 'hold', label: labels.hold },
				{ value: 'done', label: '완료' },
			];
		},
	},
	created() {
		this.init();
	},
	methods: {
		// CE/CH 새글(hit): 판정은 서버(SP_BOARD_LIST is_new)가 확정한다 — 프런트 store 의
		// 내 uid 는 로그인 직후 비동기로 늦게 채워져 자기 글 오판이 있었다(Codex 헌트).
		isNewPost(item) {
			return item.is_new === 'Y';
		},
		init() {
			this.$apiGET('/admin/api/board/category').then(re => {
				if (!re || !re.list) return;
				this.cateList = re.list;
				// 목록 → 상세 → 뒤로가기 시 보던 탭을 유지한다(회의/공지 확인 흐름에서 매번
				// 첫 탭으로 튕기면 쓰기 어렵다). 쿼리스트링이 단일 출처.
				const q = this.$route.query.cate;
				const found = this.cateList.find(c => String(c.board_category_uid) === String(q));
				this.cateUid = found ? found.board_category_uid : (this.cateList[0] && this.cateList[0].board_category_uid);
				if (this.cateUid) this.getItemList(1);
			});
		},
		changeCate(uid) {
			if (this.cateUid == uid) return;
			this.cateUid = uid;
			this.searchInfo.status = '';
			this.$router.replace({ name: 'Board', query: { cate: uid } });
			this.getItemList(1);
		},
		changeStatus(v) {
			this.searchInfo.status = v;
			this.getItemList(1);
		},
		btnSearch() {
			this.searchInfo.mode = this.searchInfo_.mode;
			this.searchInfo.keyword = this.searchInfo_.keyword;
			this.getItemList(1);
		},
		btnSearchReset() {
			this.searchInfo_ = { mode: 'title', keyword: '' };
			this.btnSearch();
		},
		getItemList(pg) {
			if (!this.cateUid) return;
			const q = new URLSearchParams({
				cateUid: this.cateUid,
				mode: this.searchInfo.mode,
				keyword: this.searchInfo.keyword || '',
				status: this.searchInfo.status || '',
				page: (pg - 1) * this.itemSize,
			});
			// 탭/검색/페이지를 빠르게 오가면 앞선 요청이 늦게 도착해 현재 화면을 덮어쓴다
			// → 마지막 요청만 반영 (Codex 헌트: done 탭에 open 목록이 그려지는 경합 실증).
			const seq = ++this.listSeq;
			this.$apiGET('/admin/api/board/list?' + q.toString()).then(data => {
				if (seq !== this.listSeq) return;
				if (!data || !data.item) return;
				this.itemList = data.item;
				this.totalCount = data.pageInfo.totalCount;
				this.page = pg;
				this.pageData = this.$pageDataSetting(this.totalCount, this.itemSize, this.blockSize, this.page);
			});
		},
		btnWrite() {
			this.$router.push({ name: 'BoardWrite', query: { cate: this.cateUid } });
		},
		btnOnDetail(id) {
			this.$router.push({ name: 'BoardDetail', params: { id }, query: { cate: this.cateUid } });
		},
		// 번호 = 현재 탭(검색·상태 필터 포함) 기준 정렬 순번. board_post_uid 는 전 게시판
		// 공용 PK 라 탭 안에서 번호가 건너뛰어 보이는 민원이 있었다 — 표시용 순번으로 대체.
		// 고정글도 자리 하나를 차지하므로("고정" 표기) 그 순번은 건너뛴다.
		rowNo(idx) {
			return this.totalCount - (this.page - 1) * this.itemSize - idx;
		},
		statusClass(v) {
			if (v === 'done') return 'txt-c--grey';
			if (v === 'hold') return 'txt-c--orange';
			return 'txt-c--blue';
		},
		// B-12(선택): "등록시 32억 → 현재 30억 (▼2억)" 변동 표시.
		priceDiff(item) {
			const snap = Number(item.snapshot_price);
			const cur = Number(item.item_sell_price);
			if (!snap || !cur || snap === cur) return null;
			const diff = cur - snap;
			return {
				text: (diff > 0 ? '▲' : '▼') + this.$formatMoney(Math.abs(diff), this.$MONEY_FORMAT_TYPE.MUCH),
				cls: diff > 0 ? 'txt-c--red' : 'txt-c--blue',
			};
		},
		openReadList(item) {
			this.$apiGET('/admin/api/board/read/list?id=' + item.board_post_uid).then(re => {
				if (!re || !re.list) return;
				this.readInfo = re;
				this.readPopup = item;
			});
		},
		btnRemind() {
			if (!this.readPopup) return;
			// 더블클릭·응답 대기 중 재클릭이 같은 미확인자에게 쪽지를 두 번 보낸다(Codex 헌트).
			if (this.reminding) return;
			if (!confirm('미확인자에게 확인 요청 쪽지를 보내시겠습니까?')) return;
			this.reminding = true;
			this.$apiPOST('/admin/api/board/read/remind', { postUid: this.readPopup.board_post_uid }).then(re => {
				this.reminding = false;
				if (!re || !re.ok) return;
				alert(re.sent + '명에게 알림을 보냈습니다.');
			});
		},
	},
};
</script>

<style scoped>
/* CE 새글 배지 — DESIGN.md 배지 규칙: 배경을 채우지 않고 1px 테두리 + 11px 글자 */
.badge-new {
	display: inline-block;
	border: 1px solid #dc3545;
	border-radius: 3px;
	color: #dc3545;
	font-size: 11px;
	line-height: 1;
	padding: 2px 4px;
	margin-right: 4px;
	vertical-align: 1px;
}
</style>
